import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Loader2, RefreshCw, Plus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useUpsertIntegration,
  useSyncIntegration,
  useDeleteIntegration,
} from "@/hooks/useIntegrations";
import type { Integration } from "@/lib/types";
import { formatError } from "@/lib/utils";

const DEFAULT_HOMESERVER = "https://matrix.beeper.com";

export function BeeperCard({ existing }: { existing: Integration | undefined }) {
  const upsert = useUpsertIntegration();
  const sync = useSyncIntegration();
  const del = useDeleteIntegration();

  const [open, setOpen] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [homeserver, setHomeserver] = useState(DEFAULT_HOMESERVER);
  const [showToken, setShowToken] = useState(false);

  const isConnected = !!existing?.enabled;
  const config = (existing?.config || {}) as {
    access_token?: string;
    homeserver_url?: string;
    matrix_user_id?: string;
    last_synced_at?: string;
  };

  const handleConnect = async () => {
    if (!accessToken.trim()) {
      toast.error("Access token is required");
      return;
    }
    try {
      await upsert.mutateAsync({
        type: "beeper",
        config: {
          access_token: accessToken.trim(),
          homeserver_url: (homeserver.trim() || DEFAULT_HOMESERVER).replace(/\/$/, ""),
          since_token: null,
        },
        enabled: true,
      });
      toast.success("Beeper connected — first sync queued");
      setOpen(false);
      setAccessToken("");
      setHomeserver(DEFAULT_HOMESERVER);
      // Kick an immediate sync so the first batch lands without waiting 3 min.
      sync.mutate("beeper", {
        onSuccess: (r) => {
          const messages = (r as { messages?: number } | null)?.messages;
          if (typeof messages === "number" && messages > 0) {
            toast.success(`First sync: ${messages} messages ingested`);
          }
        },
        onError: () => {/* sync card surfaces errors */},
      });
    } catch (err) {
      toast.error(`Connect failed: ${formatError(err)}`);
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync("beeper");
      const r = result as { status?: string; messages?: number; rooms?: number; error?: string } | null;
      if (r?.error) {
        toast.error(`Sync error: ${r.error}`);
      } else {
        toast.success(`Synced ${r?.messages ?? 0} messages from ${r?.rooms ?? 0} rooms`);
      }
    } catch (err) {
      toast.error(`Sync failed: ${formatError(err)}`);
    }
  };

  const handleDisconnect = async () => {
    if (!existing) return;
    if (!confirm("Disconnect Beeper? Existing messages stay in your inbox; new ones stop syncing.")) return;
    try {
      await del.mutateAsync(existing.id);
      toast.success("Beeper disconnected");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">Beeper</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Cloud iMessage / SMS / WhatsApp / Signal / Telegram / Instagram bridge — no Mac required.
                </CardDescription>
              </div>
            </div>
            {isConnected && (
              <Badge variant="outline" className="text-xs text-green-500 border-green-500/30">
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {isConnected ? (
            <>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {config.matrix_user_id && (
                  <p>
                    <span className="text-foreground">Account:</span>{" "}
                    <span className="font-mono">{config.matrix_user_id}</span>
                  </p>
                )}
                <p>
                  {existing?.last_synced_at
                    ? `Last synced ${formatDistanceToNow(new Date(existing.last_synced_at), { addSuffix: true })}`
                    : "Never synced"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSync} disabled={sync.isPending}>
                  {sync.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Sync Now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={handleDisconnect}
                  disabled={del.isPending}
                >
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" className="w-full" onClick={() => setOpen(true)}>
                <Plus className="h-3 w-3 mr-1" />
                Connect Beeper
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Sign up at{" "}
                <a
                  href="https://www.beeper.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  beeper.com
                </a>{" "}
                — then paste your Matrix access token here.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Connect Beeper</SheetTitle>
            <SheetDescription>
              Beeper is a Matrix homeserver. Paste your access token (found in the Beeper Desktop app under Settings → Account, or at beeper.com/account).
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs">Access token</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type={showToken ? "text" : "password"}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="syt_..."
                  className="font-mono text-xs"
                />
                <Button size="icon" variant="outline" onClick={() => setShowToken((v) => !v)}>
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted in your HO account. We use it to poll Beeper's Matrix homeserver every 3 minutes.
              </p>
            </div>

            <div>
              <Label className="text-xs">Homeserver URL (advanced)</Label>
              <Input
                value={homeserver}
                onChange={(e) => setHomeserver(e.target.value)}
                placeholder={DEFAULT_HOMESERVER}
                className="mt-1 font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Defaults to Beeper Cloud. Set this if you self-host Matrix (Element, Synapse, etc).
              </p>
            </div>

            <Button onClick={handleConnect} disabled={upsert.isPending || !accessToken.trim()} className="w-full">
              {upsert.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Connect
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
